import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useLocation } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import { IoMdLock, IoMdUnlock, IoIosHourglass } from "react-icons/io";
import { GoCheckCircleFill } from "react-icons/go";
import { HiOutlineViewGridAdd } from "react-icons/hi";
import { FaRegEdit } from "react-icons/fa";
import Swal from "sweetalert2";

import {
  Button,
  Flex,
  Text,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Tooltip,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";

import * as Yup from "yup";

import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RelatorioService from "../../../main/services/RelatorioService";

import { useAuth } from "react-oidc-context";
import { IoHelpCircle } from "react-icons/io5";
import { PopoverGenero } from "../PopoverGenero";
import { format } from "date-fns";
import { StatusAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/StatusAba";
import { LocalizacaoServicoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/LocalizacaoServicoAba";
import { OrgaosAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/OrgaosAba";
import { CaracterizacaoServicoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/CaracterizacaoServicoAba";
import { PerfilAtendimentoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/PerfilAtendimentoAba";
import { VagasDisponiveisAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/VagasDisponiveisAba";
import { RegularidadeAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/RegularidadeAba";
import { PlanejamentoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/PlanejamentoAba";
import { EquipeReferenciaAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/EquipeReferenciaAba";
import { VoluntariadoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/VoluntariadoAba";
import { ProgramaApadrinhamentoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/ProgramaApadrinhamentoAba";
import { dashboardFunc } from "../../../helpers/Funcionalidades";

const schema = Yup.object().shape({
  statusRelatorio: Yup.array()
    .nullable()
    .min(1, "Este campo é obrigatório!")
    .required("Este campo é obrigatório!"),

  idGenero: Yup.array().when("radioGenero", {
    is: (val) => val && (val === "AS" || val === "NA"),
    then: (schema) =>
      schema
        .min(1, "Este campo é obrigatório!")
        .required("Este campo é obrigatório!"),
    otherwise: (schema) => schema.nullable(),
  }),

  idadeInicial: Yup.string()
    .nullable()
    .when("radioFaixaEtaria", {
      is: (val) => val && val === "AT",
      then: (schema) => schema.required("Este campo é obrigatório!"),
      otherwise: (schema) => schema.nullable(),
    }),

  idadeFinal: Yup.string()
    .nullable()
    .when("radioFaixaEtaria", {
      is: (val) => val && val === "AT",
      then: (schema) =>
        schema
          .required("Este campo é obrigatório!")
          .test(
            "idade-max-maior-ou-igual-min",
            "A Idade Inicial não pode ser menor que a Idade Final",
            function (value) {
              const { idadeInicial } = this.parent;
              if (!value || !idadeInicial) return true;
              return Number(value) >= Number(idadeInicial);
            },
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  idadeMinima: Yup.string()
    .nullable()
    .when("radioFaixaEtaria", {
      is: (val) => val && val === "CR",
      then: (schema) => schema.required("Este campo é obrigatório!"),
      otherwise: (schema) => schema.nullable(),
    }),

  idadeMaxima: Yup.string()
    .nullable()
    .when("radioFaixaEtaria", {
      is: (val) => val && val === "CR",
      then: (schema) =>
        schema
          .required("Este campo é obrigatório!")
          .test(
            "idade-max-maior-ou-igual-min",
            "A Idade Máxima não pode ser menor que a Idade Mínima",
            function (value) {
              const { idadeMinima } = this.parent;
              if (!value || !idadeMinima) return true;
              return Number(value) >= Number(idadeMinima);
            },
          ),
      otherwise: (schema) => schema.nullable(),
    }),
});

export const FiltersRelatorioServicoAcolhimento = ({
  entidade,
  data,
  setData,
  setLoading,
  roleKeycloack,
  exclusao,
  atualizarDadosTabela,
  setMostrarTabela,
  mostrarTabela,
  children,
  loading,
}) => {
  const formRef = useRef(null);

  const location = useLocation();
  const initialLoad = useRef(true);
  const auth = useAuth();
  const token = auth?.user?.access_token;
  const [innerTabIndex, setInnerTabIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    return tempObj?.activeTabIndex ?? 0;
  });
  const [passedTabs, setPassedTabs] = useState(() => {
    const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    return tempObj?.passedTabs ?? [0];
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hintPreliminar, setHintPreliminar] = useState(false);
  const placement = useBreakpointValue({ md: "right" });
  const showTabPanelServicoGestor =
    roleKeycloack?.includes(dashboardFunc.abasServicoConselho) ||
    roleKeycloack?.includes(dashboardFunc.abasGestorDemais);

  const showTabPanelGestorDemais = roleKeycloack?.includes(
    dashboardFunc.abasGestorDemais,
  );

  const ocultarTabPanelDefensoria = roleKeycloack?.includes(
    dashboardFunc.abasDefensoriaOcultar,
  );

  const tabs = useMemo(() => {
    let visibletabs = [
      { label: "Status", fields: ["statusRelatorio"] },
      {
        label: "Localização do Serviço de Acolhimento",
        fields: [
          "idBairro",
          "idEstado",
          "idMunicipio",
          "idsServicosAcolhimento",
        ],
      },
      {
        label: "Órgãos",
        fields: ["idMunicipioOrgao, idPromotoriaOrgaos", "idVaraOrgaos"],
      },
      {
        label: "Caracterização do Serviço de Acolhimento",
        fields: ["naturezaServico", "regime", "modalidade"],
      },
      {
        label: "Perfil de Atendimento",
        fields: [
          "radioGenero",
          "radioFaixaEtaria",
          "radioSaudeCriancaDeficiencia",
          "radioSaudeSubstanciasPsicoativas",
          "radioSaudeCriancaCuidados",
          "idadeMinima",
          "idadeMaxima",
          "idGenero",
          "idadeInicial",
          "idadeFinal",
        ],
      },
      {
        label: "Regularidade",
        fields: [
          "registroCMDCA",
          "registroValidoCMDCA",
          "inscricaoCMDCA",
          "inscricaoValidaCMDCA",
          "inscricaoCMAS",
          "inscricaoValidaCMAS",
        ],
      },
      {
        label: "Planejamento",
        fields: [
          "possuiPlano",
          "possuiProjeto",
          "possuiRelatorio",
          "possuiRegimento",
        ],
      },
      { label: "Equipe de Referência", fields: ["possuiAcessoMCA"] },
      { label: "Voluntariado", fields: ["possuiVoluntariado"] },
      {
        label: "Programa de Apadrinhamento",
        fields: ["mantemPrograma", "encontraInscritoCMDCA"],
      },
      { label: "Vagas Disponíveis", fields: ["possuiVaga"] },
    ];

    if (roleKeycloack?.includes(dashboardFunc.abasServicoConselho)) {
      const allowedLabels = [
        "Status",
        "Localização do Serviço de Acolhimento",
        "Órgãos",
        "Caracterização do Serviço de Acolhimento",
        "Perfil de Atendimento",
        "Vagas Disponíveis",
      ];

      return visibletabs.filter((tab) => allowedLabels.includes(tab.label));
    }

    if (roleKeycloack?.includes(dashboardFunc.abasDefensoriaOcultar)) {
      visibletabs = visibletabs.filter(
        (tab) =>
          tab.label !== "Equipe de Referência" && tab.label !== "Voluntariado",
      );
    }

    return visibletabs;
  }, [roleKeycloack]);

  const EMPTY_FILTER_VALUES = {
    statusRelatorio: "",
    idBairro: [],
    idEstado: [
      [
        {
          id: 33,
          nome: "RIO DE JANEIRO",
          sigla: "RJ",
        },
      ],
    ],
    idMunicipio: [],
    idsServicosAcolhimento: "",
    idMunicipioOrgao: [],
    idPromotoriaOrgaos: [],
    idVaraOrgaos: [],
    naturezaServico: "",
    regime: "",
    modalidade: "",
    registroCMDCA: [],
    registroValidoCMDCA: [],
    inscricaoCMDCA: [],
    inscricaoValidaCMDCA: [],
    inscricaoCMAS: [],
    inscricaoValidaCMAS: [],
    possuiPlano: [],
    possuiProjeto: [],
    possuiRelatorio: [],
    possuiRegimento: [],
    possuiAcessoMCA: [],
    possuiVoluntariado: [],
    mantemPrograma: [],
    encontraInscritoCMDCA: [],
    possuiVaga: "",
    radioGenero: "",
    radioFaixaEtaria: "",
    radioSaudeCriancaDeficiencia: [],
    radioSaudeSubstanciasPsicoativas: [],
    radioSaudeCriancaCuidados: [],
    idadeMinima: "",
    idadeMaxima: "",
    idadeInicial: "",
    idadeFinal: "",
    idGenero: "",
    pageSize: 10,
    page: 0,
  };

  const [initialValues, setInitialValues] = useState({
    ...EMPTY_FILTER_VALUES,
  });

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    const storage = sessionStorage.getItem(entidade + "-filter");

    if (storage) {
      const tempObj = JSON.parse(storage);

      const sanitizeAges = (obj) => ({
        ...obj,
        idadeMinima:
          obj?.idadeMinima === null ||
          obj?.idadeMinima === "null" ||
          obj?.idadeMinima === undefined
            ? ""
            : obj.idadeMinima,
        idadeMaxima:
          obj?.idadeMaxima === null ||
          obj?.idadeMaxima === "null" ||
          obj?.idadeMaxima === undefined
            ? ""
            : obj.idadeMaxima,
        idadeInicial:
          obj?.idadeInicial === null ||
          obj?.idadeInicial === "null" ||
          obj?.idadeInicial === undefined
            ? ""
            : obj.idadeInicial,
        idadeFinal:
          obj?.idadeFinal === null ||
          obj?.idadeFinal === "null" ||
          obj?.idadeFinal === undefined
            ? ""
            : obj.idadeFinal,
      });

      if (Array.isArray(tempObj.idGenero) && tempObj.idGenero?.length > 0) {
        tempObj.idGenero = tempObj.idGenero.map((grupo) =>
          grupo.map((genero) => ({
            ...genero,
            label: (
              <HStack justifyContent="space-between" px={{ md: "10px" }}>
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  {genero.genero}
                </Text>
                <Tooltip label={genero.descricao} fontSize="md">
                  <span>
                    <Icon
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          })),
        );
      }

      const sanitizedValues = sanitizeAges(tempObj);

      setInitialValues(sanitizedValues);
    }
  }, [entidade]);

  useEffect(() => {
    if (!initialLoad.current) {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    } else {
      initialLoad.current = false;
    }
  }, [location]);

  const FocusClearField = () => {
    const { values, setFieldValue, validateForm, setFieldTouched } =
      useFormikContext();

    useEffect(() => {
      const isSomenteD =
        Array.isArray(values?.statusRelatorio) &&
        values.statusRelatorio.length === 1 &&
        values.statusRelatorio[0] === "D";

      if (!isSomenteD) return;

      if (values.possuiVaga !== "") {
        setFieldValue("possuiVaga", "");
      }

      setPassedTabs((prev) => {
        const vagasTabIndex = tabs.findIndex(
          (tab) => tab.label === "Vagas Disponíveis",
        );
        if (vagasTabIndex === -1) return prev;

        if (!prev.includes(vagasTabIndex)) return prev;

        return prev.filter((tabIndex) => tabIndex !== vagasTabIndex);
      });
    }, [values.statusRelatorio, values.possuiVaga, setFieldValue]);

    useEffect(() => {
      if (values?.radioFaixaEtaria === "TO") {
        setFieldValue("idadeMinima", "");
        setFieldValue("idadeMaxima", "");
        setFieldValue("idadeInicial", "");
        setFieldValue("idadeFinal", "");
      } else if (values?.radioFaixaEtaria === "AT") {
        setFieldTouched("idadeInicial", true);
        setFieldTouched("idadeFinal", true);
        setFieldValue("idadeMinima", "");
        setFieldValue("idadeMaxima", "");
      } else if (values?.radioFaixaEtaria === "CR") {
        setFieldTouched("idadeMinima", true);
        setFieldTouched("idadeMaxima", true);
        setFieldValue("idadeInicial", "");
        setFieldValue("idadeFinal", "");
      }
    }, [
      values?.radioFaixaEtaria,
      setFieldValue,
      setFieldTouched,
      validateForm,
    ]);

    useEffect(() => {
      if (values?.radioFaixaEtaria === "SR") {
        setFieldValue("idadeMinima", "");
        setFieldValue("idadeMaxima", "");
      } else if (values?.radioFaixaEtaria === "CR") {
        setFieldTouched("idadeMinima", true);
        setFieldTouched("idadeMaxima", true);
      }
    }, [
      values?.radioFaixaEtaria,
      setFieldValue,
      setFieldTouched,
      validateForm,
    ]);

    useEffect(() => {
      if (!values?.mantemPrograma.includes("S")) {
        setFieldValue("encontraInscritoCMDCA", []);
      }
    }, [values?.mantemPrograma, setFieldValue]);

    useEffect(() => {
      if (
        values?.naturezaServico?.[0]?.length === 1 &&
        values?.naturezaServico?.[0]?.[0]?.id === "publica"
      ) {
        setFieldValue("registroCMDCA", []);
        setFieldValue("registroValidoCMDCA", []);
        setFieldValue("inscricaoCMAS", []);
        setFieldValue("inscricaoValidaCMAS", []);
      }
    }, [values?.naturezaServico, setFieldValue]);

    useEffect(() => {
      const isSomenteSAF =
        values?.regime?.[0]?.length === 1 &&
        values?.regime?.[0]?.[0]?.id === "SAF";

      if (isSomenteSAF && values?.possuiVoluntariado !== "") {
        setFieldValue("possuiVoluntariado", "");
      }
    }, [values?.regime, values?.possuiVoluntariado, setFieldValue]);

    useEffect(() => {
      if (values?.radioGenero === "SR") {
        setFieldValue("idGenero", "");
      } else if (values?.radioGenero === "AS" || values?.radioGenero === "NA") {
        setFieldTouched("idGenero", true);
      }
    }, [values?.radioGenero, setFieldValue, setFieldTouched]);

    useEffect(() => {
      if (mostrarTabela) {
        setActiveTabIndex(tabs.length);
        setFieldValue("activeTabIndex", tabs.length);
        if (innerTabIndex === 0) setInnerTabIndex(0);
      }
    }, [setFieldValue, values?.activeTabIndex]);

    return null;
  };

  const renderizarTooltip = () => {
    if (isMobile)
      return (
        <PopoverGenero
          isOpen={hintPreliminar}
          setIsOpen={setHintPreliminar}
          flex={true}
          handleClick={() => setHintPreliminar(!hintPreliminar)}
          labelTextoGenero={
            "Selecione o Município para habilitar a(s) Promotoria(s) de Justiça e Vara(s) da Infância correspondente(s)."
          }
        />
      );

    return (
      <Tooltip
        label={
          "Selecione o Município para habilitar a(s) Promotoria(s) de Justiça e Vara(s) da Infância correspondente(s)."
        }
        fontSize="md"
        maxW="400px"
        textAlign={"justify"}
        placement={placement}
      >
        <span className="hint-tooltip">
          <Icon
            aria-label="Aviso de pendente"
            color="#6B6C71"
            as={IoHelpCircle}
            boxSize={{ base: "20px" }}
          />
        </span>
      </Tooltip>
    );
  };

  const fieldHasValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "boolean") return true;
    if (value === 0) return true;
    return !!value;
  };

  const isTabComplete = (index, props) => {
    const tab = tabs[index];

    if (
      tab.label === "Vagas Disponíveis" &&
      Array.isArray(props.values?.statusRelatorio) &&
      props.values.statusRelatorio.length === 1 &&
      props.values.statusRelatorio[0] === "D"
    ) {
      return false;
    }

    const fields = tab.fields.flatMap((f) =>
      f.split(",").map((name) => name.trim()),
    );

    const hasError = fields.some((field) => props.errors?.[field]);

    const hasAnyValue = fields.some((field) =>
      fieldHasValue(props.values?.[field]),
    );

    return passedTabs.includes(index) && hasAnyValue && !hasError;
  };

  const funcPesquisarRelatorioPendencia = useCallback(
    (valuesToSend) => {
      let request =
        RelatorioService.getRelatorioServicoAcolhimento(valuesToSend);

      request
        .then((res) => {
          sessionStorage.setItem(entidade + "-filterSearch", true);
          const tempObj = JSON.parse(
            sessionStorage.getItem(entidade + "-filter"),
          );

          sessionStorage.setItem(
            entidade + "-filter",
            JSON.stringify({
              ...tempObj,
              totalItems: res.data.totalElements,
              totalPages: res.data.totalPages,
            }),
          );

          setData(res.data);
          setMostrarTabela(true);
          sessionStorage.setItem(
            "mostrarTabelaRelatorio",
            JSON.stringify(true),
          );
        })
        .catch((error) => {
          setData([]);
          Swal.fire({
            title: "Atenção!",
            text: error.response.data.message
              ? error.response.data.message
              : "",
            icon: "error",
            confirmButtonText: "Fechar",
            confirmButtonColor: "#169499",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [entidade, setData, setLoading, setMostrarTabela],
  );

  useEffect(() => {
    const tempObj = JSON.parse(sessionStorage.getItem("table-" + entidade));
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    const pesquisou = sessionStorage.getItem(entidade + "-filterSearch");
    const passou = sessionStorage.getItem("crianca-passou");
    const servicoPassou = sessionStorage.getItem("servico-passou");

    if (atualizarDadosTabela || (pesquisou && (passou || servicoPassou))) {
      setLoading(true);

      const statusMap = {
        A: "ATIVO",
        D: "DESATIVADO",
        S: "SUSPENSO",
      };
      const status = storage?.statusRelatorio.map((item) => statusMap[item]);
      const generos = (storage?.idGenero || [])
        ?.flat()
        ?.map((item) => item.genero)
        ?.filter(Boolean);
      const nomeServicosAcolhimentos = (storage?.idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.nomeFantasia)
        ?.filter(Boolean);
      const municipiosOrgaos = (storage?.idMunicipioOrgao || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const promotoriasOrgaos = (storage?.idPromotoriaOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const varasOrgaos = (storage?.idVaraOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const naturezas = (storage?.naturezaServico || [])
        ?.flat()
        ?.map((item) => item?.id?.toUpperCase())
        ?.filter((id) => id !== "" && id != null);
      const regimes = (storage?.regime || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const modalidades = (storage?.modalidade || [])
        ?.flat()
        ?.map((item) => item.descricao)
        ?.filter(Boolean);

      const SAF =
        Array.isArray(storage?.regime) &&
        storage?.regime?.flat()?.some((item) => item.id === "SAF");

      const idadeMin = [storage?.idadeInicial, storage?.idadeMinima].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const idadeMax = [storage?.idadeFinal, storage?.idadeMaxima].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const valuesToSend = {
        status: status,
        localizacaoServicoAcolhimento: {
          estados: storage?.idEstado
            ? storage?.idEstado
                .flat()
                .map((item) => item.id)
                .filter((item) => item.id !== "")
            : [],
          municipios: storage?.idMunicipio
            ? storage?.idMunicipio
                .flat()
                .map((item) => ({
                  id: item.id,
                  idEstado: item.idEstado,
                }))
                .filter((item) => item.id !== "")
            : [],
          bairros: storage?.idBairro
            ? storage?.idBairro
                .flat()
                .map((item) => ({
                  id: item.id,
                  idMunicipio: item.idMunicipio,
                }))
                .filter((item) => item.id !== "")
            : [],
          nomeServicosAcolhimentos: nomeServicosAcolhimentos,
        },
        orgaos: {
          municipios: municipiosOrgaos,
          promotorias: promotoriasOrgaos,
          varasInfancias: varasOrgaos,
        },
        caracterizacaoServicoAcolhimento: {
          regimes: regimes,
          modalidades:
            SAF &&
            !modalidades.includes("Não se aplica") &&
            modalidades?.length > 0
              ? [...modalidades, "Não se aplica"]
              : modalidades,
          naturezas: naturezas,
        },
        perfilAtendimento: {
          ...(storage?.radioGenero && {
            tipoGenero:
              storage.radioGenero === "AS"
                ? "ATENDE"
                : storage.radioGenero === "SR"
                  ? "SEM_RESTRICAO"
                  : storage.radioGenero === "NA"
                    ? "NAO_ATENDE"
                    : "",
          }),

          generos: generos.length > 0 ? generos : [],

          ...(storage?.radioFaixaEtaria && {
            restricao:
              storage.radioFaixaEtaria === "TO"
                ? "TODOS"
                : storage.radioFaixaEtaria === "AT"
                  ? "ATENDE"
                  : storage.radioFaixaEtaria === "CR"
                    ? "COM_RESTRICAO"
                    : "",
          }),

          ...(idadeMin && {
            idadeMinima: Number(idadeMin),
          }),

          ...(idadeMax && {
            idadeMaxima: Number(idadeMax),
          }),

          restricaoDeficiencia:
            storage?.radioSaudeCriancaDeficiencia?.length > 0
              ? storage?.radioSaudeCriancaDeficiencia
              : [],

          restricaoSubstancias:
            storage?.radioSaudeSubstanciasPsicoativas?.length > 0
              ? storage?.radioSaudeSubstanciasPsicoativas
              : [],

          restricaoCuidados:
            storage?.radioSaudeCriancaCuidados?.length > 0
              ? storage?.radioSaudeCriancaCuidados
              : [],
        },

        regularidade: {
          registroCMDCA: storage?.registroCMDCA ? storage?.registroCMDCA : [],
          registroValidoCMDCA: storage?.registroValidoCMDCA
            ? storage?.registroValidoCMDCA
            : [],
          inscricaoCMDCA: storage?.inscricaoCMDCA
            ? storage?.inscricaoCMDCA
            : [],
          inscricaoValidaCMDCA: storage?.inscricaoValidaCMDCA
            ? storage?.inscricaoValidaCMDCA
            : [],
          inscricaoCMAS: storage?.inscricaoCMAS ? storage?.inscricaoCMAS : [],
          inscricaoValidaCMAS: storage?.inscricaoValidaCMAS
            ? storage?.inscricaoValidaCMAS
            : [],
        },

        planejamento: {
          possuiPlanoTrabalho: storage?.possuiPlano ? storage?.possuiPlano : [],
          possuiProjetoPoliticoPedagogico: storage?.possuiProjeto
            ? storage?.possuiProjeto
            : [],
          possuiRelatorioAnual: storage?.possuiRelatorio
            ? storage?.possuiRelatorio
            : [],
          possuiRegimentoInterno: storage?.possuiRegimento
            ? storage?.possuiRegimento
            : [],
        },

        possuiAcessoMCA: storage?.possuiAcessoMCA
          ? storage?.possuiAcessoMCA
          : [],
        possuiVoluntariado: storage?.possuiVoluntariado
          ? storage?.possuiVoluntariado
          : [],

        apadrinhamento: {
          mantemProgramaApadrinhamento: storage?.mantemPrograma
            ? storage?.mantemPrograma
            : [],
          inscritoCMDCA: storage?.encontraInscritoCMDCA
            ? storage?.encontraInscritoCMDCA
            : [],
        },

        ...((storage?.possuiVaga === "S" || storage?.possuiVaga === "N") && {
          vagasDisponiveis: storage?.possuiVaga === "S",
        }),
        pageSize: storage?.pageSize ? storage.pageSize : "",
        page: storage?.page ? storage.page : 0,
        sortField: tempObj ? tempObj[0]?.id : "servicoAcolhimentoNomeFantasia",
        sortType: tempObj ? (tempObj[0]?.desc ? "DESC" : "ASC") : "ASC",
      };

      funcPesquisarRelatorioPendencia(valuesToSend);
    }
  }, [
    exclusao,
    atualizarDadosTabela,
    location.state,
    entidade,
    funcPesquisarRelatorioPendencia,
    setLoading,
    roleKeycloack,
  ]);

  const clearAllFieldsWithSetFieldValue = (props) => {
    props.resetForm({
      values: {
        ...EMPTY_FILTER_VALUES,
        activeTabIndex: 0,
      },
    });

    setTimeout(() => {
      props.validateForm();
    }, 0);
  };

  function ClearFiltersButton({ tabs, tabLabel, props }) {
    const handleClear = () => {
      const selectedTab = tabs.find((tab) => tab.label === tabLabel);

      if (!selectedTab) return;

      selectedTab.fields.forEach((fieldName) => {
        const cleanNames = fieldName.split(",").map((f) => f.trim());

        cleanNames.forEach((f) => {
          props.setFieldValue(f, "");
        });
      });

      setTimeout(() => {
        props.validateForm();
      }, 0);
    };

    return (
      <Button
        type="button"
        px={4}
        h={"28px"}
        color="#169499"
        border="1px solid #169499"
        backgroundColor="#EBF3F2"
        fontWeight={1000}
        fontSize="14px"
        isDisabled={props.isSubmitting || loading}
        borderRadius={"99px"}
        _hover={{ bg: "#DDEBEA" }}
        onClick={handleClear}
      >
        Limpar Filtros
      </Button>
    );
  }

  const handleSubmit = (values, actions, helpers) => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    setTimeout(() => {
      const {
        statusRelatorio,
        idEstado,
        idMunicipio,
        idBairro,
        possuiVaga,
        idGenero,
        radioGenero,
        idMunicipioOrgao,
        idPromotoriaOrgaos,
        idVaraOrgaos,
        regime,
        naturezaServico,
        idsServicosAcolhimento,
        modalidade,
        radioFaixaEtaria,
        idadeInicial,
        idadeFinal,
        idadeMinima,
        idadeMaxima,
        radioSaudeCriancaDeficiencia,
        radioSaudeCriancaCuidados,
        radioSaudeSubstanciasPsicoativas,
        registroCMDCA,
        registroValidoCMDCA,
        inscricaoCMDCA,
        inscricaoValidaCMDCA,
        inscricaoCMAS,
        inscricaoValidaCMAS,
        possuiPlano,
        possuiProjeto,
        possuiRelatorio,
        possuiRegimento,
        possuiAcessoMCA,
        possuiVoluntariado,
        mantemPrograma,
        encontraInscritoCMDCA,
      } = values;

      values.page = 0;
      values.pageSize = storage?.pageSize ? storage.pageSize : 10;

      const statusMap = {
        A: "ATIVO",
        D: "DESATIVADO",
        S: "SUSPENSO",
      };
      const status = statusRelatorio.map((item) => statusMap[item]);
      const generos = (idGenero || [])
        ?.flat()
        ?.map((item) => item.genero)
        ?.filter(Boolean);
      const nomeServicosAcolhimentos = (idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.nomeFantasia)
        ?.filter(Boolean);
      const municipiosOrgaos = (idMunicipioOrgao || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const promotoriasOrgaos = (idPromotoriaOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const varasOrgaos = (idVaraOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const naturezas = (naturezaServico || [])
        ?.flat()
        ?.map((item) => item?.id?.toUpperCase())
        ?.filter((id) => id !== "" && id != null);
      const regimes = (regime || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const modalidades = (modalidade || [])
        ?.flat()
        ?.map((item) => item.descricao)
        ?.filter(Boolean);

      const SAF =
        Array.isArray(regime) &&
        regime?.flat()?.some((item) => item.id === "SAF");

      const idadeMin = [idadeInicial, idadeMinima].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const idadeMax = [idadeFinal, idadeMaxima].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const valuesToSend = {
        status: status,
        localizacaoServicoAcolhimento: {
          estados: idEstado
            ? idEstado
                .flat()
                .map((item) => item.id)
                .filter((item) => item.id !== "")
            : [],
          municipios: idMunicipio
            ? idMunicipio
                .flat()
                .map((item) => ({
                  id: item.id,
                  idEstado: item.idEstado,
                }))
                .filter((item) => item.id !== "")
            : [],
          bairros: idBairro
            ? idBairro
                .flat()
                .map((item) => ({
                  id: item.id,
                  idMunicipio: item.idMunicipio,
                }))
                .filter((item) => item.id !== "")
            : [],
          nomeServicosAcolhimentos: nomeServicosAcolhimentos,
        },
        orgaos: {
          municipios: municipiosOrgaos,
          promotorias: promotoriasOrgaos,
          varasInfancias: varasOrgaos,
        },
        caracterizacaoServicoAcolhimento: {
          regimes: regimes,
          modalidades:
            SAF &&
            !modalidades.includes("Não se aplica") &&
            modalidades?.length > 0
              ? [...modalidades, "Não se aplica"]
              : modalidades,
          naturezas: naturezas,
        },
        perfilAtendimento: {
          ...(radioGenero && {
            tipoGenero:
              radioGenero === "AS"
                ? "ATENDE"
                : radioGenero === "SR"
                  ? "SEM_RESTRICAO"
                  : radioGenero === "NA"
                    ? "NAO_ATENDE"
                    : "",
          }),

          generos: generos.length > 0 ? generos : [],

          ...(radioFaixaEtaria && {
            restricao:
              radioFaixaEtaria === "TO"
                ? "TODOS"
                : radioFaixaEtaria === "AT"
                  ? "ATENDE"
                  : radioFaixaEtaria === "CR"
                    ? "COM_RESTRICAO"
                    : "",
          }),

          ...(idadeMin && {
            idadeMinima: Number(idadeMin),
          }),

          ...(idadeMax && {
            idadeMaxima: Number(idadeMax),
          }),

          restricaoDeficiencia:
            radioSaudeCriancaDeficiencia?.length > 0
              ? radioSaudeCriancaDeficiencia
              : [],

          restricaoSubstancias:
            radioSaudeSubstanciasPsicoativas?.length > 0
              ? radioSaudeSubstanciasPsicoativas
              : [],

          restricaoCuidados:
            radioSaudeCriancaCuidados?.length > 0
              ? radioSaudeCriancaCuidados
              : [],
        },

        regularidade: {
          registroCMDCA: registroCMDCA ? registroCMDCA : [],
          registroValidoCMDCA: registroValidoCMDCA ? registroValidoCMDCA : [],
          inscricaoCMDCA: inscricaoCMDCA ? inscricaoCMDCA : [],
          inscricaoValidaCMDCA: inscricaoValidaCMDCA
            ? inscricaoValidaCMDCA
            : [],
          inscricaoCMAS: inscricaoCMAS ? inscricaoCMAS : [],
          inscricaoValidaCMAS: inscricaoValidaCMAS ? inscricaoValidaCMAS : [],
        },

        planejamento: {
          possuiPlanoTrabalho: possuiPlano ? possuiPlano : [],
          possuiProjetoPoliticoPedagogico: possuiProjeto ? possuiProjeto : [],
          possuiRelatorioAnual: possuiRelatorio ? possuiRelatorio : [],
          possuiRegimentoInterno: possuiRegimento ? possuiRegimento : [],
        },

        possuiAcessoMCA: possuiAcessoMCA ? possuiAcessoMCA : [],
        possuiVoluntariado: possuiVoluntariado ? possuiVoluntariado : [],

        apadrinhamento: {
          mantemProgramaApadrinhamento: mantemPrograma ? mantemPrograma : [],
          inscritoCMDCA: encontraInscritoCMDCA ? encontraInscritoCMDCA : [],
        },
        ...((possuiVaga === "S" || possuiVaga === "N") && {
          vagasDisponiveis: possuiVaga === "S",
        }),
        pageSize: storage?.pageSize ? storage.pageSize : 10,
        page: 0,
      };

      if (entidade === "relatorio-servico-acolhimento") {
        values.desc = false;
        values.sort = "servicoAcolhimentoNomeFantasia";
        valuesToSend.sortField = "servicoAcolhimentoNomeFantasia";
        valuesToSend.sortType = "ASC";
        sessionStorage.setItem(
          "table-relatorio-servico-acolhimento",
          JSON.stringify([
            { id: "servicoAcolhimentoNomeFantasia", desc: false },
          ]),
        );
      }

      setLoading(true);
      sessionStorage.setItem(
        entidade + "-filter",
        JSON.stringify({ ...values, passedTabs }),
      );

      if (entidade === "relatorio-servico-acolhimento") {
        funcPesquisarRelatorioPendencia(valuesToSend);
      }

      actions.setSubmitting(false);
    }, 200);
  };

  return (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        innerRef={formRef}
        enableReinitialize
        validateOnMount
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <FocusClearField />

            <Tabs
              orientation="vertical"
              variant="unstyled"
              width="100%"
              index={activeTabIndex}
            >
              <Box
                display="flex"
                width="100%"
                flexDirection={{ base: "column", lg: "row" }}
              >
                {innerTabIndex !== 1 && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    borderRight={{ base: "none", lg: "1px solid #E2E8F0" }}
                    borderBottom={{ base: "1px solid #E2E8F0", lg: "none" }}
                    mb={{ base: "10px", lg: 0 }}
                    height="auto"
                    flexShrink={0}
                  >
                    <TabList
                      display="flex"
                      flexDirection="column"
                      pb={6}
                      mb={3}
                      borderBottom="1px solid #E2E8F0"
                      gap="2"
                    >
                      {tabs.map((tab, index) => {
                        const active = props.values.activeTabIndex ?? 0;

                        const isSomenteSAF =
                          props?.values?.regime?.[0]?.length === 1 &&
                          props?.values?.regime?.[0]?.[0]?.id === "SAF";

                        const isVoluntariadoBloqueado =
                          tab?.label === "Voluntariado" && isSomenteSAF;
                        return (
                          <Tab
                            key={index}
                            isDisabled={
                              (index !== 0 &&
                                !props?.values?.statusRelatorio?.length) ||
                              (tab?.label === "Vagas Disponíveis" &&
                                Array.isArray(props?.values?.statusRelatorio) &&
                                props.values.statusRelatorio.length === 1 &&
                                props.values.statusRelatorio[0] === "D") ||
                              isVoluntariadoBloqueado
                            }
                            justifyContent="space-between"
                            alignItems="center"
                            border={`1px solid ${
                              passedTabs.includes(index) &&
                              isTabComplete(index, props) &&
                              index !== active
                                ? "#79ADD8"
                                : "#CBD5E0"
                            }`}
                            color={
                              index === active
                                ? "#3C8ACC"
                                : isTabComplete(index, props) &&
                                    passedTabs.includes(index)
                                  ? "#169499"
                                  : "#A3A7A7"
                            }
                            mx={4}
                            my={1}
                            bgColor={
                              isTabComplete(index, props) && index !== active
                                ? "#DDEBEA"
                                : "#F0F0F0"
                            }
                            borderRadius="99px"
                            py="10px"
                            px="14px"
                            fontWeight="600"
                            opacity={
                              index === 0 ||
                              (props?.values?.statusRelatorio?.length &&
                                !isVoluntariadoBloqueado)
                                ? 1
                                : 0.5
                            }
                            cursor={
                              index === 0 ||
                              (props?.values?.statusRelatorio?.length &&
                                !isVoluntariadoBloqueado)
                                ? "pointer"
                                : "not-allowed"
                            }
                            _selected={{
                              bg: "blue.50",
                              borderColor: "blue.400",
                            }}
                            onClick={() => {
                              setActiveTabIndex(index);
                              props.setFieldValue("activeTabIndex", index);
                              setMostrarTabela(false);

                              const current =
                                JSON.parse(
                                  sessionStorage.getItem(entidade + "-filter"),
                                ) || {};

                              sessionStorage.setItem(
                                entidade + "-filter",
                                JSON.stringify({
                                  ...current,
                                  activeTabIndex: index,
                                  passedTabs: passedTabs.includes(index)
                                    ? passedTabs
                                    : [...passedTabs, index],
                                }),
                              );

                              if (!passedTabs.includes(index)) {
                                setPassedTabs((prev) => [...prev, index]);
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap="2">
                              {index === active ? (
                                <FaRegEdit size={24} />
                              ) : (
                                <HiOutlineViewGridAdd size={24} />
                              )}

                              {tab.label}
                            </Box>

                            {tabs[index].fields.some(
                              (field) => props.errors?.[field],
                            ) && (
                              <Box
                                fontSize="12px"
                                lineHeight={"100%"}
                                fontWeight={600}
                                color="#992525"
                                px="8px"
                                py="4px"
                                borderRadius="100px"
                                bgColor={"#FFD8DB"}
                              >
                                Obrigatório
                              </Box>
                            )}

                            {(() => {
                              const active = props.values.activeTabIndex ?? 0;

                              if (
                                isTabComplete(index, props) &&
                                index !== active
                              ) {
                                return (
                                  <GoCheckCircleFill size={24} color="green" />
                                );
                              }

                              if (
                                index === active &&
                                !(
                                  tabs[index].fields.some(
                                    (field) => props.errors?.[field],
                                  ) ||
                                  (tab.label === "Status" &&
                                    !isTabComplete(index, props))
                                )
                              ) {
                                return <IoIosHourglass size={24} />;
                              }

                              if (index !== 0) {
                                const isVagasBloqueada =
                                  tab?.label === "Vagas Disponíveis" &&
                                  Array.isArray(
                                    props?.values?.statusRelatorio,
                                  ) &&
                                  props.values.statusRelatorio.length === 1 &&
                                  props.values.statusRelatorio[0] === "D";

                                const isVoluntariadoBloqueado =
                                  tab?.label === "Voluntariado" &&
                                  props?.values?.regime?.[0]?.length === 1 &&
                                  props?.values?.regime?.[0]?.[0]?.id === "SAF";

                                if (
                                  isVagasBloqueada ||
                                  isVoluntariadoBloqueado
                                ) {
                                  return <IoMdLock size={24} />;
                                }

                                return props?.values?.statusRelatorio?.length >
                                  0 ? (
                                  <IoMdUnlock size={24} />
                                ) : (
                                  <IoMdLock size={24} />
                                );
                              }
                            })()}
                          </Tab>
                        );
                      })}
                    </TabList>

                    <Box p="4" display="flex" flexDirection="column" gap="2">
                      <Button
                        type="submit"
                        translate="no"
                        color="#FFF"
                        backgroundColor="#169499"
                        fontWeight={700}
                        fontSize="15px"
                        lineHeight="21.6px"
                        borderRadius="99px"
                        isDisabled={mostrarTabela}
                        _hover={{ bg: "#169499" }}
                        isLoading={props.isSubmitting || loading}
                        w={"100%"}
                      >
                        Gerar Relatório
                      </Button>

                      <Button
                        type="button"
                        px={2}
                        py={5}
                        color="#169499"
                        border="1px solid #169499"
                        backgroundColor="#EBF3F2"
                        fontWeight={1000}
                        fontSize="14px"
                        lineHeight="21.6px"
                        borderRadius="99px"
                        isDisabled={props.isSubmitting || loading}
                        _hover={{ bg: "#DDEBEA" }}
                        onClick={() => {
                          sessionStorage.removeItem(entidade + "-filter");
                          sessionStorage.removeItem("servico-passou");

                          setPassedTabs([0]);
                          setActiveTabIndex(0);
                          setInnerTabIndex(0);

                          clearAllFieldsWithSetFieldValue(props);
                          props.setFieldValue("activeTabIndex", 0);
                          props.setFieldValue("statusRelatorio", 0);

                          setMostrarTabela(false);
                        }}
                      >
                        Limpar todos os filtros
                      </Button>
                    </Box>
                  </Box>
                )}
                <Flex w="full" minW={0}>
                  <TabPanels>
                    {showTabPanelServicoGestor && (
                      <TabPanel px={10} py={0}>
                        <StatusAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Status"}
                            props={props}
                          />
                        </StatusAba>
                      </TabPanel>
                    )}
                    {showTabPanelServicoGestor && (
                      <TabPanel px={10} py={0}>
                        <LocalizacaoServicoAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Localização do Serviço de Acolhimento"}
                            props={props}
                          />
                        </LocalizacaoServicoAba>
                      </TabPanel>
                    )}
                    {showTabPanelServicoGestor && (
                      <TabPanel px={10} py={0}>
                        <OrgaosAba
                          props={props}
                          renderizarTooltip={renderizarTooltip}
                          token={token}
                        >
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Órgãos"}
                            props={props}
                          />
                        </OrgaosAba>
                      </TabPanel>
                    )}
                    {showTabPanelServicoGestor && (
                      <TabPanel px={10} py={0}>
                        <CaracterizacaoServicoAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={
                              "Caracterização do Serviço de Acolhimento"
                            }
                            props={props}
                          />
                        </CaracterizacaoServicoAba>
                      </TabPanel>
                    )}
                    {showTabPanelServicoGestor && (
                      <TabPanel px={10} py={0}>
                        <PerfilAtendimentoAba props={props} isMobile={isMobile}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Perfil de Atendimento"}
                            props={props}
                          />
                        </PerfilAtendimentoAba>
                      </TabPanel>
                    )}
                    {showTabPanelGestorDemais && (
                      <TabPanel px={10} py={0}>
                        <RegularidadeAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Regularidade"}
                            props={props}
                          />
                        </RegularidadeAba>
                      </TabPanel>
                    )}
                    {showTabPanelGestorDemais && (
                      <TabPanel px={10} py={0}>
                        <PlanejamentoAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Planejamento"}
                            props={props}
                          />
                        </PlanejamentoAba>
                      </TabPanel>
                    )}
                    {showTabPanelGestorDemais && !ocultarTabPanelDefensoria && (
                      <TabPanel px={10} py={0}>
                        <EquipeReferenciaAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Equipe de Referência"}
                            props={props}
                          />
                        </EquipeReferenciaAba>
                      </TabPanel>
                    )}
                    {showTabPanelGestorDemais && !ocultarTabPanelDefensoria && (
                      <TabPanel px={10} py={0}>
                        <VoluntariadoAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Voluntariado"}
                            props={props}
                          />
                        </VoluntariadoAba>
                      </TabPanel>
                    )}

                    {!roleKeycloack?.includes(
                      dashboardFunc.abasServicoConselho,
                    ) && (
                      <TabPanel px={10} py={0}>
                        <ProgramaApadrinhamentoAba props={props}>
                          <ClearFiltersButton
                            tabs={tabs}
                            tabLabel={"Programa de Apadrinhamento"}
                            props={props}
                          />
                        </ProgramaApadrinhamentoAba>
                      </TabPanel>
                    )}

                    <TabPanel px={10} py={0}>
                      <VagasDisponiveisAba props={props}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel="Vagas Disponíveis"
                          props={props}
                        />
                      </VagasDisponiveisAba>
                    </TabPanel>
                    <TabPanel mt={innerTabIndex === 1 && 6} py={0}>
                      <Tabs
                        variant="enclosed"
                        isFitted
                        index={innerTabIndex}
                        onChange={(i) => setInnerTabIndex(i)}
                      >
                        <TabList
                          display="flex"
                          alignItems="center"
                          gap={4}
                          flexWrap={{ base: "wrap", xl: "nowrap" }}
                        >
                          <Flex w="full">
                            <Tab
                              maxW="219px"
                              color="#575553"
                              _selected={{
                                border: "1px solid #DDEBEA",
                                borderBottom: "4px solid #169499",
                                color: "#169499",
                                fontWeight: 700,
                                bgColor: "#16949908",
                              }}
                            >
                              <Flex flexDirection="column" alignItems="center">
                                <Text fontSize="16px">Relatório Gerado</Text>
                                <Text
                                  color="#6A6A6AD9"
                                  fontWeight={300}
                                  fontSize="13px"
                                >{`Data: ${format(
                                  new Date(),
                                  "dd/MM/yyyy",
                                )}`}</Text>
                              </Flex>
                            </Tab>
                            <Tab
                              maxW="219px"
                              color="#575553"
                              _selected={{
                                border: "1px solid #DDEBEA",
                                borderBottom: "4px solid #169499",
                                color: "#169499",
                                fontWeight: 700,
                                bgColor: "#16949908",
                              }}
                            >
                              <Flex flexDirection="column" alignItems="center">
                                <Text fontSize="16px">Filtros Preenchidos</Text>
                                <Text
                                  fontSize="13px"
                                  fontWeight={300}
                                  color="#6A6A6AD9"
                                >
                                  Para Geração do Relatório
                                </Text>
                              </Flex>
                            </Tab>
                          </Flex>

                          <Button
                            type="button"
                            translate="no"
                            color="#FFF"
                            backgroundColor="#169499"
                            fontWeight={700}
                            fontSize="15px"
                            lineHeight="21.6px"
                            _hover={{ bg: "#169499" }}
                            w={"100%"}
                            maxW={"145px"}
                            onClick={() => {
                              sessionStorage.removeItem(entidade + "-filter");
                              sessionStorage.removeItem("servico-passou");

                              setPassedTabs([0]);
                              setActiveTabIndex(0);
                              setInnerTabIndex(0);

                              clearAllFieldsWithSetFieldValue(props);
                              props.setFieldValue("activeTabIndex", 0);
                              props.setFieldValue("statusRelatorio", 0);

                              setMostrarTabela(false);
                            }}
                          >
                            Nova Pesquisa
                          </Button>
                        </TabList>

                        {children}
                      </Tabs>
                    </TabPanel>
                  </TabPanels>
                </Flex>
              </Box>
            </Tabs>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};
